import { useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { Decimal } from "decimal.js";
import {
  fetchApiGet,
  fetchApiPut,
  fetchApiPost,
  fetchApiDelete,
} from "../../utils";
import { zodEmptyStringToUndefined } from "@/lib/prelude";
import { transformToDecimal } from "../../common";
import { BaseDbObject } from "@/lib/models/db";

export const CreateCropSettingValidation = z.object({
  commodity_name: z.string().min(1),
  commodity_type: z.string().min(1),
  crop_year: z.number().int().min(2000).max(2500).optional(),
  option_month: z.string().optional(),
  customer_id: z.preprocess(
    zodEmptyStringToUndefined,
    z.string().uuid().optional()
  ),
  implied_basis: z.union([z.number(), z.string(), z.null(), z.instanceof(Decimal)]).optional()
    .transform((val) => {
      if (val === "") return undefined;
      return val;
    })
    .transform(transformToDecimal({ throwOnInvalid: true, key: "implied_basis" })),
  is_enabled: z.boolean().default(true),
});

export const UpdateCropSettingValidation = z.object({
  option_month: z.string().optional(),
  implied_basis: z.union([z.number(), z.string(), z.null(), z.instanceof(Decimal)]).optional()
    .transform((val) => {
      if (val === "") return null;
      return val;
    })
    .transform(transformToDecimal({ throwOnInvalid: true, key: "implied_basis" })),
  is_enabled: z.boolean(),
});

export const ListCropSettingsValidation = z.object({
  commodity_name: z.string().optional(),
  commodity_type: z.string().optional(),
  crop_year: z.number().int().optional(),
  is_enabled: z.boolean().optional(),
});

export const GetCropSettingHistoryValidation = z.object({
  limit: z.number().min(1).default(50).optional(),
  offset: z.number().min(0).default(0).optional(),
});

export interface CropSetting extends BaseDbObject {
  commodity_name: string;
  commodity_type: string;
  crop_year?: number;

  option_month?: string;
  customer_id?: string;

  implied_basis: Decimal | null;
  is_enabled: boolean;

  Customer?: { id: string; long_name: string; }
}

export interface CropSettingHistory {
  id: string;
  created_at: Date;
  updated_at: Date;
  user_id: string;
  setting_id: string;
  commodity_name: string;
  commodity_type: string;
  crop_year?: number;
  option_month?: string;
  customer_id?: string;

  implied_basis: Decimal | null;
  is_enabled: boolean;
  history_type: "created" | "updated" | "deleted";
  User?: {
    id: string;
    name: string;
  };
}

export function useListCropSettings({ filters }: { filters?: z.infer<typeof ListCropSettingsValidation> } = {}) {
  const queryParams = new URLSearchParams();

  if (filters) {
    if (filters.commodity_name) {
      queryParams.set("commodity_name", filters.commodity_name);
    }
    if (filters.commodity_type) {
      queryParams.set("commodity_type", filters.commodity_type);
    }
    if (filters.crop_year) {
      queryParams.set("crop_year", filters.crop_year.toString());
    }
    if (filters.is_enabled !== undefined) {
      queryParams.set("is_enabled", filters.is_enabled.toString());
    }
  }

  return useQuery({
    queryKey: ["risk", "crop-settings", queryParams.toString()],
    queryFn: () =>
      fetchApiGet<CropSetting[]>("risk/crop-settings", queryParams),
    retry: 2,
    keepPreviousData: true,
  });
}

export function useGetCropSetting({ settingId }: { settingId?: string }) {
  return useQuery({
    queryKey: ["risk", "crop-settings", "single", settingId],
    queryFn: () =>
      fetchApiGet<CropSetting>(`risk/crop-settings/${settingId}`),
    retry: 2,
    enabled: !!settingId,
    keepPreviousData: true,
  });
}

export function useCreateCropSetting() {
  const queryClient = useQueryClient();

  return async (data: z.infer<typeof CreateCropSettingValidation>) => {
    const result = await fetchApiPost<CropSetting>(
      "risk/crop-settings",
      data,
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["risk"] });
    }
    return result;
  };
}

export function useUpdateCropSetting() {
  const queryClient = useQueryClient();

  return async (settingId: string, data: z.infer<typeof UpdateCropSettingValidation>) => {
    const result = await fetchApiPut<CropSetting>(
      `risk/crop-settings/${settingId}`,
      data,
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["risk"] });
    }
    return result;
  };
}

export function useDeleteCropSetting() {
  const queryClient = useQueryClient();

  return async (settingId: string) => {
    const result = await fetchApiDelete<CropSetting>(
      `risk/crop-settings/${settingId}`,
    );

    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["risk"] });
    }
    return result;
  };
}

export function useGetCropSettingHistory({ settingId, filters }: { settingId?: string; filters?: z.infer<typeof GetCropSettingHistoryValidation> } = {}) {
  const queryParams = new URLSearchParams();

  if (filters) {
    if (filters.limit) {
      queryParams.set("limit", filters.limit.toString());
    }
    if (filters.offset) {
      queryParams.set("offset", filters.offset.toString());
    }
  }

  return useQuery({
    queryKey: ["risk", "crop-settings", "history", settingId, queryParams.toString()],
    queryFn: () =>
      fetchApiGet<CropSettingHistory[]>(`risk/crop-settings/${settingId}/history`, queryParams),
    retry: 2,
    enabled: !!settingId,
    keepPreviousData: true,
  });
}
